import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Masonry from 'react-masonry-css'
import classNames from 'classnames'
import LoadingIndicator from '@/components/ui/status/LoadingIndicator'
import Button from '@/components/ui/button'
import { useScrollPositionAtBottom } from '@/hooks'
import LazyLoadImport from 'react-lazyload'

const LazyLoad = LazyLoadImport.default ?? LazyLoadImport
import './InfiniteGrid.scss'
import { i18n } from '@/locale/i18n'
import { useAutoAnimate } from '@formkit/auto-animate/react'

const InfiniteGrid = props => {
  const {
    className,
    columnClassName,
    containerClassName = '',
    error,
    isLoading,
    loadedRows,
    onLoadMore,
    onRowRender,
    placeholder,
    style,
    totalRows,
    autoLoading = true,
    /** Holds scroll-triggered loading for a moment without swapping the footer for "Load more". */
    autoLoadPaused = false,
    /** False while the host swaps one layout for another, so the rows cut over. */
    animateRows = true,
    useMasonry = false,
    useTable = false,
    skeletonRows = 0,
    onRowSkeletonRender,
    useLazyLoad = false,
    renderTableHeader = null,
    breakpoints = { default: 1 },
    onBreakpointChange = null,
    onViewAllClick = null, // Optional callback when "View all" button is clicked
    viewAllButtonText = null, // Optional custom text for the view all button
    footerClassNames = '',
    gridInsert = null,
    // Callers that remount the grid on a filter change must turn this off.
    // auto-animate reparents rows, and unmounting that parent throws
    // removeChild, which the route error boundary shows as the error page.
    autoAnimate = true,
  } = props

  const container = useRef()
  // Prefetch the next page well before the user hits the true bottom so feed /
  // category infinite scroll does not stall on the network round-trip.
  const preloadThresholdPx = 1600
  const isScrollAtBottom = useScrollPositionAtBottom(container, preloadThresholdPx)
  const [currentBreakpoint, setCurrentBreakpoint] = useState(null)
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)

  const [animationParent] = useAutoAnimate({
    duration: 300,
    easing: 'ease-in-out'
  })
  const [masonryAnimationParent, enableMasonryAnimation] = useAutoAnimate({
    duration: 300,
    easing: 'ease-in-out'
  })

  /* Rows swapped wholesale — a different layout for the same list — have nothing to
     animate between, and auto-animate would slide the old shapes into the new ones. Coming
     back, the rows that replace the placeholders land one frame before animation resumes,
     so they cut in rather than fade. */
  const masonryAnimationPaused = useRef(false)
  useLayoutEffect(() => {
    if (!animateRows) {
      masonryAnimationPaused.current = true
      enableMasonryAnimation(false)
      return undefined
    }
    if (!masonryAnimationPaused.current) {
      enableMasonryAnimation(true)
      return undefined
    }

    const frame = requestAnimationFrame(() => {
      masonryAnimationPaused.current = false
      enableMasonryAnimation(true)
    })
    return () => cancelAnimationFrame(frame)
  }, [animateRows, enableMasonryAnimation])

  // Safari-specific fix: Force initial load when container is mounted
  useEffect(() => {
    if (!hasInitiallyLoaded && container.current && !isLoading && loadedRows === 0 && totalRows > 0) {
      // Delay slightly to ensure Safari has properly rendered the container
      const timer = setTimeout(() => {
        onLoadMore({ startIndex: 0 })
        setHasInitiallyLoaded(true)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [container.current, hasInitiallyLoaded, isLoading, loadedRows, totalRows])

  useEffect(() => {
    if (
      autoLoading &&
      !autoLoadPaused &&
      isScrollAtBottom &&
      !isLoading &&
      (totalRows == null || loadedRows < totalRows)
    ) {
      onLoadMore({ startIndex: loadedRows })
    }
    // onLoadMore is often an inline lambda from parents; omit it to avoid fetch loops.
  }, [isScrollAtBottom, isLoading, loadedRows, totalRows, autoLoading, autoLoadPaused])

  // Determine current breakpoint based on window width
  useEffect(() => {
    if (!onBreakpointChange) return

    const handleResize = () => {
      if (typeof window === 'undefined') return

      const width = window.innerWidth
      let newBreakpoint = 'default'

      // Find the appropriate breakpoint based on window width
      const breakpointKeys = Object.keys(breakpoints)
        .filter(key => key !== 'default')
        .map(Number)
        .sort((a, b) => a - b) // Sort in ascending order 
      for (const bp of breakpointKeys) {
        if (width <= bp) {
          newBreakpoint = bp
          break
        }
      }

      if (newBreakpoint !== currentBreakpoint) {
        setCurrentBreakpoint(newBreakpoint)
        onBreakpointChange(breakpoints[newBreakpoint] || breakpoints.default)
      }
    }

    handleResize() // Initial check
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [breakpoints, onBreakpointChange])

  // First N items render immediately so above-the-fold cards show without waiting for scroll
  const MASONRY_PRE_RENDER_ROWS = 12
  const columnCount = (currentBreakpoint && breakpoints[currentBreakpoint]) || breakpoints.default

  /* The insert is a grid item like any card: it takes one column's width and
     sits after `afterRows` full rows, so its slot scales with the column count.
     It waits until the grid has loaded that far rather than being pinned to the end. */
  const insertAt = gridInsert?.element && gridInsert.afterRows > 0 ? columnCount * gridInsert.afterRows : -1
  const showInsert = insertAt >= 0 && loadedRows >= insertAt

  const renderMasonryItems = () => {
    const items = []
    const pushInsert = () =>
      items.push(
        <div key="masonry-insert" className="infinite-grid__insert">
          {gridInsert.element}
        </div>
      )

    for (let index = 0; index < loadedRows; index++) {
      if (showInsert && index === insertAt) pushInsert()

      const row = onRowRender({ index })
      if (index < MASONRY_PRE_RENDER_ROWS) {
        items.push(<React.Fragment key={`masonry-item-${index}`}>{row}</React.Fragment>)
      } else {
        items.push(
          <LazyLoad key={`masonry-item-${index}`} height={300} once>
            {row}
          </LazyLoad>
        )
      }
    }

    if (showInsert && insertAt >= loadedRows) pushInsert()

    return items
  }

  const renderMasonryContent = () => {
    const masonryProps = {
      breakpointCols: breakpoints,
      className: 'infinite-grid__content',
      columnClassName: classNames('infinite-grid__column', columnClassName),
    }

    return (
      <div ref={autoAnimate ? masonryAnimationParent : undefined} className="w-full">
        <Masonry {...masonryProps}>{renderMasonryItems()}</Masonry>
      </div>
    )
  }

  const renderListContent = () => {
    const content = [] 
    
    for (let index = 0; index < loadedRows; index++) {
      if (useLazyLoad) {
        content.push(
          <LazyLoad overflow key={`list-item-${index}`}>
            {onRowRender({ index })}
          </LazyLoad>
        )
      } else {
        content.push(
          <div 
            key={`list-item-${index}`}
            style={{ '--animation-delay': `${(index % 3) * 100}ms` }}
            className="grid-item-animated"
          >
            {onRowRender({ index })}
          </div>
        )
      }
    }
    return (
      <div ref={autoAnimate ? animationParent : undefined} className="infinite-grid__results">
        {content}
      </div>
    )
  }
  const renderTableContent = () => {
    const content = []
    for (let index = 0; index < loadedRows; index++) {
      if (useLazyLoad) {
        content.push(
          <LazyLoad overflow key={`table-item-${index}`}>
            {onRowRender({ index })}
          </LazyLoad>
        )
      } else {
        const rowContent = onRowRender({ index })
        // Ensure table rows have proper keys
        if (rowContent) {
        content.push(
          React.cloneElement(rowContent, {
              key: rowContent.key || `table-row-${index}`
            })
          )
        }
      }
    }
    return (
      <table className={classNames('infinite-table', columnClassName)}>
        {renderTableHeader}
        <tbody ref={autoAnimate ? animationParent : undefined}>{content}</tbody>
      </table>
    )
  }

  const renderFooter = () => {
    // Show skeletons during pagination if we have skeleton renderer
    if (isLoading && onRowSkeletonRender && loadedRows > 0) {
      const skeletonsToShow = skeletonRows > 0 ? skeletonRows : 3
      const skeletons = [...Array(skeletonsToShow).keys()].map(index => 
        React.cloneElement(onRowSkeletonRender(index), { key: `pagination-skeleton-${index}` })
      )
      
      // For table layout, wrap tr elements in a table structure
      if (useTable) {
        return (
          <table className={classNames('infinite-table', columnClassName)}>
            <tbody>{skeletons}</tbody>
          </table>
        )
      }
      
      return <div className="infinite-grid__skeletons">{skeletons}</div>
    }
    // Show skeletons during initial load if configured
    if (isLoading && skeletonRows > 0 && onRowSkeletonRender && loadedRows === 0) {
      const skeletons = [...Array(skeletonRows).keys()].map(index => 
        React.cloneElement(onRowSkeletonRender(index), { key: `initial-skeleton-${index}` })
      )
      
      // For table layout, wrap tr elements in a table structure
      if (useTable) {
        return (
          <table className={classNames('infinite-table', columnClassName)}>
            <tbody>{skeletons}</tbody>
          </table>
        )
      }
      
      return <div className="infinite-grid__skeletons">{skeletons}</div>
    }
    // Fallback to loading indicator
    if (isLoading && autoLoading) {
      return <LoadingIndicator className="infinite-grid__loading" mode="list" />
    }

    if (error) {
      return (
        <div className="infinite-grid__footer-container">
          <Button className="infinite-grid__button" size="large" type="outlined" onClick={onLoadMore}>
            {i18n('infinite_grid.retry')}
          </Button>
        </div>
      )
    }

    if (isLoading && totalRows === 0) {
      return <LoadingIndicator className="infinite-grid__loading" mode="list" />
    }

    if (placeholder && totalRows === 0) {
      return <div className="infinite-grid__placeholder">{placeholder}</div>
    }

    if (!autoLoading && loadedRows < totalRows) {
      // If onViewAllClick callback is provided, show "View all" button
      if (onViewAllClick) {
        return (
          <div className="infinite-grid__footer-container">
            <Button
              loading={isLoading}
              className="infinite-grid__button"
              size="large"
              variant="white"
              onClick={onViewAllClick}>
              {viewAllButtonText || i18n('infinite_grid.view_all_markets')}
            </Button>
          </div>
        )
      }
      
      // Otherwise, show regular "Load more" button
      return (
        <div className="infinite-grid__footer-container">
          <Button
            loading={isLoading}
            className="infinite-grid__button"
            size="large"
            variant="white"
            disabled={loadedRows >= totalRows}
            onClick={() => onLoadMore({ startIndex: loadedRows })}>
            {i18n('infinite_grid.load_more')}
          </Button>
        </div>
      )
    }

    return null
  }

  return (
    <div ref={container} className={classNames('infinite-grid', className)} style={style}>
      <div className={classNames('infinite-grid__content', containerClassName)}>
        {useMasonry ? renderMasonryContent() : useTable ? renderTableContent() : renderListContent()}
      </div>
      {/* Not auto-animated: the footer holds one of several mutually exclusive states —
          skeletons, a spinner, a load-more button, nothing — so there is nothing to
          interpolate between, and auto-animate also animates the parent's own reposition.
          A loaded page pushes the footer down the full height of the rows it added, which
          it played as a page-wide bar flying up to ten thousand pixels in 250ms. */}
      <div className={`infinite-grid__footer ${footerClassNames}`}>{renderFooter()}</div>
    </div>
  )
}

export default InfiniteGrid
